/**
 * Sprint S-3 · #27 · PWA Update Toast + Offline Indicator.
 *
 * Detecta dos eventos:
 *  1) Nueva versión del SW lista → toast "Nueva versión disponible · Actualizar"
 *     que al pulsarse envía SKIP_WAITING al SW pendiente y recarga la página.
 *  2) Online/offline → banner inferior cuando no hay red.
 *
 * Se monta una sola vez (en App.js).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, WifiSlash } from "@phosphor-icons/react";

function reloadWithNewSW(waiting) {
  // Mandar al SW pendiente que active el skipWaiting
  try { waiting.postMessage({ type: "SKIP_WAITING" }); } catch {}
  // El SW activará y emitirá controllerchange → recargamos
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Pequeño delay para no race-conditionar
    setTimeout(() => window.location.reload(), 150);
  }, { once: true });
}

function watchServiceWorkerUpdates() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistration().then((reg) => {
    if (!reg) return;
    // Caso 1: ya hay un SW esperando al cargar
    if (reg.waiting && navigator.serviceWorker.controller) {
      showUpdateToast(reg.waiting);
    }
    // Caso 2: se descubre un update en runtime
    reg.addEventListener("updatefound", () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          showUpdateToast(installing);
        }
      });
    });
  }).catch(() => {});
}

function showUpdateToast(waitingSW) {
  toast(
    "Nueva versión disponible",
    {
      description: "Pulsa para actualizar y obtener las últimas mejoras.",
      duration: Infinity,
      action: {
        label: "Actualizar",
        onClick: () => reloadWithNewSW(waitingSW),
      },
      icon: <ArrowClockwise size={18} weight="bold" />,
      id: "ag-sw-update",
    }
  );
}

export function PwaUpdateToast() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    watchServiceWorkerUpdates();
    // Poll cada 60 min para detectar updates aunque el usuario tenga la pestaña abierta toda la sesión
    const interval = setInterval(() => {
      navigator.serviceWorker?.getRegistration().then((r) => r?.update().catch(() => {}));
    }, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  if (online) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-4 z-[90] bg-[#1A1A1A] text-white px-4 py-2.5 rounded-full shadow-xl flex items-center gap-2 text-xs font-semibold border border-amber-400"
      role="status"
      aria-live="polite"
      data-testid="pwa-offline-banner"
    >
      <WifiSlash size={14} weight="fill" className="text-amber-400" />
      Sin conexión · trabajando con datos guardados
    </div>
  );
}
